import { useState, useEffect, useCallback, useRef } from 'react'
import { withTimeout } from '../lib/asyncWithTimeout'

const DEFAULT_TIMEOUT_MS = 15_000
/** Minimaal zo lang in loading-state voordat tab-focus een herpoging triggert */
const STUCK_BEFORE_VISIBLE_RETRY_MS = 3_000

export interface UseAsyncDataOptions {
  timeoutMs?: number
  /** Herlaad bij tab-focus of bfcache als laden vastzit */
  retryOnVisible?: boolean
  enabled?: boolean
}

export interface UseAsyncDataResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => void
}

/**
 * Laadt async data met timeout, foutstatus en automatische retry
 * wanneer een mobiele tab uit bfcache terugkomt of lang blijft hangen.
 */
export function useAsyncData<T> (
  loader: () => Promise<T>,
  deps: unknown[],
  options: UseAsyncDataOptions = {}
): UseAsyncDataResult<T> {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retryOnVisible = true,
    enabled = true,
  } = options

  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)

  const loaderRef = useRef(loader)
  loaderRef.current = loader

  const loadingRef = useRef(loading)
  loadingRef.current = loading

  const loadingStartedAtRef = useRef(Date.now())
  const requestIdRef = useRef(0)

  const runLoad = useCallback(async (opts?: { silent?: boolean }) => {
    if (!enabled) {
      setLoading(false)
      return
    }

    const silent = opts?.silent ?? false
    const requestId = ++requestIdRef.current

    if (!silent) {
      loadingStartedAtRef.current = Date.now()
      setLoading(true)
    }
    setError(null)

    try {
      const result = await withTimeout(loaderRef.current(), timeoutMs)
      if (requestId !== requestIdRef.current) return
      setData(result)
    } catch (e) {
      if (requestId !== requestIdRef.current) return
      setError(e instanceof Error ? e.message : 'Fout bij laden')
    } finally {
      if (requestId === requestIdRef.current && !silent) {
        setLoading(false)
      }
    }
  }, [enabled, timeoutMs])

  useEffect(() => {
    void runLoad()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps komen van de caller
  }, [...deps, runLoad])

  const reload = useCallback(() => {
    void runLoad()
  }, [runLoad])

  useEffect(() => {
    if (!retryOnVisible || !enabled) return

    function retryIfStuck (): void {
      if (document.visibilityState !== 'visible') return
      if (!loadingRef.current) return
      const stuckMs = Date.now() - loadingStartedAtRef.current
      if (stuckMs >= STUCK_BEFORE_VISIBLE_RETRY_MS) {
        void runLoad()
      }
    }

    function handlePageShow (event: PageTransitionEvent): void {
      if (event.persisted && loadingRef.current) {
        void runLoad()
      }
    }

    document.addEventListener('visibilitychange', retryIfStuck)
    window.addEventListener('pageshow', handlePageShow)
    return () => {
      document.removeEventListener('visibilitychange', retryIfStuck)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [retryOnVisible, enabled, runLoad])

  return { data, loading, error, reload }
}
